import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  getBezierPath,
  type Node,
  type Edge,
  type NodeProps,
  type EdgeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Activity, WifiOff, Wifi, FlaskConical } from 'lucide-react';
import { useStore } from '../services/store';
import { subscribeNodeGraphEvents } from '../services/nodeGraphEvents';

// ── Types ─────────────────────────────────────────────────────────────────────
type NodeId = 'ai-hub' | 'notebooklm' | 'agentic-cluster' | 'rag-kbs' | 'youwee';
type NodeStatus = 'idle' | 'active' | 'processing' | 'error';

interface Task { id: string; text: string; ts: number; }

interface AgentNodeData extends Record<string, unknown> {
  nodeId: NodeId;
  label: string;
  sublabel: string;
  icon: string;
  status: NodeStatus;
  tasks: Task[];
  provider?: string;
  color: string;
  glowColor: string;
}

interface AgentEdgeData extends Record<string, unknown> {
  active: boolean;
  direction: 'forward' | 'backward' | 'both';
  label: string;
  color: string;
}

interface LogEntry {
  id: string;
  ts: number;
  text: string;
  color: string;
}

// ── Node visual config ────────────────────────────────────────────────────────
const NODE_CONFIG: Record<NodeId, { label: string; sublabel: string; icon: string; color: string; glowColor: string }> = {
  'ai-hub':           { label: 'AI Hub',          sublabel: 'Codex · Claude · Gemini', icon: '⚡', color: '#d4a017', glowColor: '#f0c040' },
  'notebooklm':       { label: 'NotebookLM',       sublabel: 'Research Node',           icon: '📒', color: '#3b9ede', glowColor: '#6bbeff' },
  'rag-kbs':          { label: 'RAG KBS',           sublabel: 'Knowledge Base',          icon: '🗄', color: '#2ab5bc', glowColor: '#5cdde4' },
  'agentic-cluster':  { label: 'Agentic Cluster',   sublabel: 'Orchestration',           icon: '🤖', color: '#8b6be8', glowColor: '#b0a0f0' },
  'youwee':           { label: 'Youwee',            sublabel: 'Video Intelligence',      icon: '🎬', color: '#e85858', glowColor: '#ff9090' },
};

// ── Edge config ───────────────────────────────────────────────────────────────
interface EdgeCfg { id: string; source: NodeId; target: NodeId; sh: string; th: string; }
const EDGE_CFGS: EdgeCfg[] = [
  { id: 'hub-nlm',  source: 'ai-hub',          target: 'notebooklm',     sh: 'sl', th: 'tr' },
  { id: 'hub-rag',  source: 'ai-hub',          target: 'rag-kbs',         sh: 'sr', th: 'tl' },
  { id: 'hub-agc',  source: 'ai-hub',          target: 'agentic-cluster', sh: 'sl', th: 'tr' },
  { id: 'hub-yw',   source: 'ai-hub',          target: 'youwee',          sh: 'sr', th: 'tl' },
  { id: 'nlm-agc',  source: 'notebooklm',      target: 'agentic-cluster', sh: 'sb', th: 'tt' },
  { id: 'rag-yw',   source: 'rag-kbs',         target: 'youwee',          sh: 'sb', th: 'tt' },
  { id: 'agc-rag',  source: 'agentic-cluster', target: 'rag-kbs',         sh: 'sr', th: 'tl' },
  { id: 'rag-kbs-nlm', source: 'rag-kbs',      target: 'notebooklm',     sh: 'st', th: 'tb' },
];

// ── Node positions ────────────────────────────────────────────────────────────
const NODE_POS: Record<NodeId, { x: number; y: number }> = {
  'ai-hub':          { x: 290, y: 185 },
  'notebooklm':      { x: 30,  y: 30  },
  'rag-kbs':         { x: 560, y: 30  },
  'agentic-cluster': { x: 30,  y: 360 },
  'youwee':          { x: 560, y: 360 },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function hexToRgb(hex: string): string {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? `${parseInt(r[1], 16)},${parseInt(r[2], 16)},${parseInt(r[3], 16)}` : '255,255,255';
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ── Builders ──────────────────────────────────────────────────────────────────
function buildInitialNodes(): Node<AgentNodeData>[] {
  return (Object.entries(NODE_POS) as [NodeId, { x: number; y: number }][]).map(([id, position]) => ({
    id,
    type: 'agentNode',
    position,
    data: { nodeId: id, ...NODE_CONFIG[id], status: 'idle' as NodeStatus, tasks: [] },
  }));
}

function buildInitialEdges(): Edge<AgentEdgeData>[] {
  return EDGE_CFGS.map(cfg => ({
    id: cfg.id,
    source: cfg.source,
    target: cfg.target,
    sourceHandle: cfg.sh,
    targetHandle: cfg.th,
    type: 'animatedEdge',
    data: { active: false, direction: 'forward' as const, label: '', color: NODE_CONFIG[cfg.source].color },
    style: { cursor: 'default' },
    interactionWidth: 0,
  }));
}

// ── Custom handle style ───────────────────────────────────────────────────────
const HS: React.CSSProperties = { width: 6, height: 6, background: 'transparent', border: 'none', minWidth: 0, minHeight: 0 };

// ── AgentNode ─────────────────────────────────────────────────────────────────
function AgentNode({ data }: NodeProps<Node<AgentNodeData>>) {
  const { label, sublabel, icon, status, tasks, provider, color, glowColor } = data;
  const isActive = status === 'active' || status === 'processing';
  const isProcessing = status === 'processing';
  const isError = status === 'error';

  const rgb = hexToRgb(color);
  const borderClr = isError ? '#e85858' : isActive ? color : 'rgba(255,255,255,0.10)';
  const shadow = isActive
    ? `0 0 18px rgba(${rgb},0.45), 0 0 36px rgba(${rgb},0.18), inset 0 0 12px rgba(${rgb},0.06)`
    : isError ? '0 0 14px rgba(232,88,88,0.35)' : '0 2px 10px rgba(0,0,0,0.55)';

  return (
    <>
      {/* Handles — all hidden, explicit IDs for edge targeting */}
      <Handle type="source"  position={Position.Left}   id="sl" style={HS} />
      <Handle type="source"  position={Position.Right}  id="sr" style={HS} />
      <Handle type="source"  position={Position.Top}    id="st" style={HS} />
      <Handle type="source"  position={Position.Bottom} id="sb" style={HS} />
      <Handle type="target"  position={Position.Left}   id="tl" style={HS} />
      <Handle type="target"  position={Position.Right}  id="tr" style={HS} />
      <Handle type="target"  position={Position.Top}    id="tt" style={HS} />
      <Handle type="target"  position={Position.Bottom} id="tb" style={HS} />

      {/* Outer glow ring (active only) */}
      {isActive && (
        <div style={{
          position: 'absolute', inset: -6, borderRadius: 18,
          border: `1px solid rgba(${rgb},0.25)`,
          boxShadow: `0 0 20px rgba(${rgb},0.15)`,
          pointerEvents: 'none',
          animation: isProcessing ? 'ring-pulse 1.8s ease-in-out infinite' : 'none',
        }} />
      )}

      {/* Node card */}
      <div style={{
        width: 190, minHeight: 118,
        background: isActive ? `rgba(${rgb},0.07)` : 'rgba(8,13,28,0.92)',
        border: `1.5px solid ${borderClr}`,
        borderRadius: 12,
        padding: '11px 13px 10px',
        boxShadow: shadow,
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.5s ease, box-shadow 0.5s ease, background 0.5s ease',
        fontFamily: "'JetBrains Mono','Fira Code','Cascadia Code',monospace",
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 7 }}>
          {/* Icon */}
          <div style={{
            fontSize: 19, lineHeight: 1, flexShrink: 0,
            filter: isActive ? `drop-shadow(0 0 7px ${color})` : 'grayscale(0.5) opacity(0.7)',
            transition: 'filter 0.5s ease',
            animation: isProcessing ? 'icon-breathe 1.4s ease-in-out infinite' : 'none',
          }}>
            {icon}
          </div>

          {/* Labels */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
              color: isActive ? color : 'rgba(255,255,255,0.8)',
              filter: isActive ? `drop-shadow(0 0 5px ${glowColor})` : 'none',
              transition: 'color 0.5s ease, filter 0.5s ease',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {label}
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {sublabel}
            </div>
          </div>

          {/* Status dot */}
          <div style={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 3,
            background: isError ? '#e85858' : isActive ? color : 'rgba(255,255,255,0.12)',
            boxShadow: isActive ? `0 0 7px ${color}, 0 0 14px rgba(${rgb},0.4)` : 'none',
            transition: 'all 0.5s ease',
            animation: isProcessing ? 'dot-blink 0.75s ease-in-out infinite'
              : isActive ? 'dot-blink 2.2s ease-in-out infinite' : 'none',
          }} />
        </div>

        {/* Provider badge */}
        {provider && isActive && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: color, background: `rgba(${rgb},0.13)`,
            border: `1px solid rgba(${rgb},0.28)`, borderRadius: 4,
            padding: '2px 7px', marginBottom: 8,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, display: 'inline-block', animation: 'dot-blink 1s ease-in-out infinite' }} />
            {provider}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: `rgba(${rgb},${isActive ? 0.18 : 0.05})`, marginBottom: 8, transition: 'background 0.5s ease' }} />

        {/* Tasks */}
        {tasks.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {tasks.slice(0, 3).map((t, i) => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 5,
                fontSize: 10, color: i === 0 ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.45)',
                lineHeight: 1.4,
                animation: i === 0 ? 'task-in 0.35s ease' : 'none',
              }}>
                <span style={{ color: i === 0 ? color : 'rgba(255,255,255,0.2)', flexShrink: 0, fontSize: 7, marginTop: 3 }}>▶</span>
                <span style={{ wordBreak: 'break-word' }}>{t.text}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'center', paddingTop: 2 }}>
            — idle —
          </div>
        )}
      </div>
    </>
  );
}

// ── AnimatedEdge ──────────────────────────────────────────────────────────────
function AnimatedEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd }: EdgeProps<Edge<AgentEdgeData>>) {
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const isActive = data?.active ?? false;
  const color = data?.color ?? '#334466';
  const label = (data?.label as string) ?? '';

  return (
    <>
      {/* Outer glow (active) */}
      {isActive && <path d={edgePath} fill="none" stroke={color} strokeWidth={14} opacity={0.08} strokeLinecap="round" />}
      {isActive && <path d={edgePath} fill="none" stroke={color} strokeWidth={7} opacity={0.18} strokeLinecap="round" />}

      {/* Main stroke */}
      <path
        d={edgePath}
        fill="none"
        stroke={isActive ? color : 'rgba(50,70,110,0.35)'}
        strokeWidth={isActive ? 2.2 : 1.2}
        strokeDasharray={isActive ? '7 4' : '3 6'}
        strokeLinecap="round"
        style={isActive ? { filter: `drop-shadow(0 0 4px ${color})` } : {}}
        markerEnd={markerEnd}
      >
        {isActive && (
          <animate
            attributeName="stroke-dashoffset"
            from="22"
            to="0"
            dur="0.9s"
            repeatCount="indefinite"
          />
        )}
      </path>

      {/* Traveling pulse dot */}
      {isActive && (
        <circle r={4.5} fill={color} opacity={0.95} style={{ filter: `drop-shadow(0 0 8px ${color}) drop-shadow(0 0 3px #fff)` }}>
          <animateMotion dur="2s" repeatCount="indefinite" path={edgePath} rotate="auto" />
        </circle>
      )}

      {/* Second slower pulse */}
      {isActive && (
        <circle r={2.5} fill={color} opacity={0.6} style={{ filter: `drop-shadow(0 0 5px ${color})` }}>
          <animateMotion dur="2s" begin="1s" repeatCount="indefinite" path={edgePath} rotate="auto" />
        </circle>
      )}

      {/* Label */}
      {isActive && label && (
        <text x={labelX} y={labelY - 11} fill={color} fontSize="9" fontWeight="700"
          textAnchor="middle" fontFamily="'JetBrains Mono',monospace" letterSpacing="0.05em"
          style={{ filter: `drop-shadow(0 0 5px ${color})` }}>
          {label}
        </text>
      )}
    </>
  );
}

// ── Type maps ─────────────────────────────────────────────────────────────────
const nodeTypes = { agentNode: AgentNode };
const edgeTypes = { animatedEdge: AnimatedEdge };

// ── CSS injected into document ────────────────────────────────────────────────
const GRAPH_CSS = `
  @keyframes dot-blink    { 0%,100%{opacity:1} 50%{opacity:0.25} }
  @keyframes icon-breathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.18)} }
  @keyframes ring-pulse   { 0%,100%{opacity:0.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.03)} }
  @keyframes task-in      { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:none} }
  @keyframes log-slide-in { from{opacity:0;transform:translateX(16px)} to{opacity:1;transform:none} }
  .react-flow__attribution   { display:none!important }
  .react-flow__handle        { opacity:0!important; pointer-events:none!important }
  .react-flow__node          { cursor:default!important }
  .react-flow__node.selected { box-shadow:none!important; outline:none!important }
  .react-flow__edge-interaction { pointer-events:none!important }
  .react-flow__controls-button  { background:rgba(10,16,34,0.9)!important; border-color:rgba(255,255,255,0.12)!important; color:rgba(255,255,255,0.7)!important }
  .react-flow__controls-button:hover { background:rgba(30,40,70,0.9)!important }
  .aag-log::-webkit-scrollbar       { width:4px }
  .aag-log::-webkit-scrollbar-track { background:transparent }
  .aag-log::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.1); border-radius:2px }
`;

// ── Legend ────────────────────────────────────────────────────────────────────
function Legend() {
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
      {[
        { label: 'Idle', color: 'rgba(255,255,255,0.15)', dot: true },
        { label: 'Active', color: '#3b9ede', dot: true },
        { label: 'Processing', color: '#d4a017', dot: true, pulse: true },
        { label: 'Error', color: '#e85858', dot: true },
      ].map(item => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
          <div style={{
            width: 7, height: 7, borderRadius: '50%', background: item.color,
            boxShadow: item.dot && item.color !== 'rgba(255,255,255,0.15)' ? `0 0 6px ${item.color}` : 'none',
            animation: item.pulse ? 'dot-blink 1s ease-in-out infinite' : 'none',
          }} />
          {item.label}
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AgentActivityGraph() {
  const { token } = useStore();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<AgentNodeData>>(buildInitialNodes());
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<AgentEdgeData>>(buildInitialEdges());
  const [connected, setConnected] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const logRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<any>(null);

  // Process snapshot diff → update graph + append log entries
  const handleSnapshot = useCallback((snap: any) => {
    const prev = prevRef.current;
    const newLog: LogEntry[] = [];
    const now = Date.now();

    if (prev) {
      // New tasks on nodes
      for (const [id, ns] of Object.entries(snap.nodes) as [NodeId, any][]) {
        const pn = prev.nodes[id];
        if (ns.tasks.length > (pn?.tasks?.length ?? 0) && ns.tasks[0]) {
          const cfg = NODE_CONFIG[id];
          newLog.push({ id: `${now}-${id}`, ts: now, text: `${cfg.icon} ${cfg.label}: ${ns.tasks[0].text}`, color: cfg.color });
        }
      }
      // Connection activations
      for (const [key, cs] of Object.entries(snap.connections) as [string, any][]) {
        const pc = prev.connections[key];
        if (cs.active && !pc?.active) {
          const [from, to] = key.split('--') as [NodeId, NodeId];
          const fc = NODE_CONFIG[from] ?? NODE_CONFIG[to];
          const tc = NODE_CONFIG[to] ?? NODE_CONFIG[from];
          if (fc && tc) {
            newLog.push({
              id: `${now}-${key}`,
              ts: now,
              text: `${fc.icon} ${fc.label} → ${tc.icon} ${tc.label}${cs.label ? `: ${cs.label}` : ''}`,
              color: fc.color,
            });
          }
        }
      }
    }

    prevRef.current = snap;

    // Update nodes
    setNodes(prev => prev.map(node => {
      const ns = snap.nodes[node.id as NodeId];
      if (!ns) return node;
      const d = node.data;
      if (d.status === ns.status && JSON.stringify(d.tasks) === JSON.stringify(ns.tasks) && d.provider === ns.provider) return node;
      return { ...node, data: { ...d, status: ns.status, tasks: ns.tasks, provider: ns.provider } };
    }));

    // Update edges
    setEdges(prev => prev.map(edge => {
      const key = `${edge.source}--${edge.target}`;
      const rKey = `${edge.target}--${edge.source}`;
      const cs = snap.connections[key] ?? snap.connections[rKey];
      if (!cs) return edge;
      const d = edge.data as AgentEdgeData | undefined;
      if (d?.active === cs.active && d?.label === cs.label) return edge;
      return {
        ...edge,
        data: {
          active: Boolean(cs.active),
          direction: cs.direction as AgentEdgeData['direction'],
          label: String(cs.label ?? ''),
          color: d?.color ?? NODE_CONFIG[edge.source as NodeId]?.color ?? '#334466',
        } satisfies AgentEdgeData,
      };
    }));

    if (newLog.length > 0) {
      setLog(prev => [...newLog.reverse(), ...prev].slice(0, 60));
      setTimeout(() => logRef.current && (logRef.current.scrollTop = 0), 20);
    }
  }, [setNodes, setEdges]);

  // The graph and job listeners share one authenticated feed per document.
  useEffect(() => subscribeNodeGraphEvents(message => {
    if (message.type === 'snapshot' || message.type === 'update') handleSnapshot(message);
  }, setConnected), [handleSnapshot]);

  // Fetch initial simulation state on mount
  useEffect(() => {
    fetch('/api/node-graph/simulation', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDemoEnabled(Boolean(d.enabled)); })
      .catch(() => {});
  }, [token]);

  // Toggle demo simulation
  const toggleDemo = async () => {
    const next = !demoEnabled;
    try {
      const r = await fetch('/api/node-graph/simulation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: next }),
      });
      if (r.ok) {
        const d = await r.json();
        setDemoEnabled(Boolean(d.enabled));
      }
    } catch { /* ignore */ }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#060d1a', color: '#fff' }}>
      <style>{GRAPH_CSS}</style>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
        background: 'rgba(6,13,26,0.95)',
      }}>
        <Activity size={15} color="#d4a017" />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.88)' }}>
          Agent Activity Graph
        </span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginLeft: 4 }}>
          Realtime node communication{demoEnabled ? ' · demo active' : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Legend />
          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)' }} />
          {/* Demo toggle */}
          <button
            onClick={toggleDemo}
            title={demoEnabled ? 'Disable demo simulation' : 'Enable demo simulation'}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid',
              transition: 'all 0.25s ease',
              ...(demoEnabled
                ? { background: 'rgba(212,160,23,0.15)', borderColor: 'rgba(212,160,23,0.5)', color: '#d4a017' }
                : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' }),
            }}
          >
            <FlaskConical size={11} />
            Demo {demoEnabled ? 'ON' : 'OFF'}
          </button>
          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {connected
              ? <Wifi size={12} color="#3b9ede" />
              : <WifiOff size={12} color="#e85858" />}
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: connected ? '#3b9ede' : '#e85858' }}>
              {connected ? 'LIVE' : 'RECONNECTING…'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ReactFlow canvas */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.12 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            zoomOnScroll
            panOnDrag
            minZoom={0.3}
            maxZoom={2.5}
            style={{ background: 'transparent' }}
            proOptions={{ hideAttribution: true }}
          >
            {/* Dot grid background */}
            <Background
              variant={BackgroundVariant.Dots}
              gap={28}
              size={1.2}
              color="rgba(255,255,255,0.055)"
            />
            <Controls
              style={{
                background: 'rgba(8,14,30,0.85)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
              }}
              showInteractive={false}
            />

            {/* Node type labels (SVG overlay) */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
              <defs>
                <filter id="fglow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
            </svg>
          </ReactFlow>

          {/* Node type decorations */}
          <div style={{
            position: 'absolute', bottom: 12, left: 12,
            display: 'flex', gap: 8, flexWrap: 'wrap',
          }}>
            {(Object.entries(NODE_CONFIG) as [NodeId, typeof NODE_CONFIG[NodeId]][]).map(([id, cfg]) => {
              const nodeState = nodes.find(n => n.id === id)?.data;
              const isActive = nodeState && (nodeState.status === 'active' || nodeState.status === 'processing');
              return (
                <div key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 10, fontFamily: 'monospace',
                  color: isActive ? cfg.color : 'rgba(255,255,255,0.3)',
                  background: isActive ? `rgba(${hexToRgb(cfg.color)},0.1)` : 'rgba(10,16,30,0.7)',
                  border: `1px solid ${isActive ? `rgba(${hexToRgb(cfg.color)},0.3)` : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: 5, padding: '3px 8px',
                  transition: 'all 0.4s ease',
                }}>
                  <span>{cfg.icon}</span>
                  <span>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Activity log ── */}
        <div style={{ width: 270, display: 'flex', flexDirection: 'column', borderLeft: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          {/* Log header */}
          <div style={{
            padding: '9px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              Activity Feed
            </span>
            <div style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}>
              {log.length} events
            </div>
          </div>

          {/* Log entries */}
          <div ref={logRef} className="aag-log" style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
            {log.length === 0 ? (
              <div style={{ padding: '24px 14px', textAlign: 'center', color: 'rgba(255,255,255,0.18)', fontSize: 11 }}>
                Waiting for activity…
              </div>
            ) : (
              log.map((entry, i) => (
                <div
                  key={entry.id}
                  style={{
                    padding: '6px 14px',
                    borderBottom: '1px solid rgba(255,255,255,0.028)',
                    animation: i === 0 ? 'log-slide-in 0.3s ease' : 'none',
                  }}
                >
                  <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(255,255,255,0.28)', marginBottom: 3 }}>
                    {fmtTime(entry.ts)}
                  </div>
                  <div style={{
                    fontSize: 11, lineHeight: 1.45,
                    color: i < 3 ? entry.color : 'rgba(255,255,255,0.5)',
                    wordBreak: 'break-word',
                    transition: 'color 1s ease',
                  }}>
                    {entry.text}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer stats */}
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(Object.entries(NODE_CONFIG) as [NodeId, typeof NODE_CONFIG[NodeId]][]).map(([id, cfg]) => {
                const nodeState = nodes.find(n => n.id === id)?.data;
                const status = nodeState?.status ?? 'idle';
                const isActive = status === 'active' || status === 'processing';
                return (
                  <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: isActive ? cfg.color : 'rgba(255,255,255,0.2)' }}>
                    <div style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: isActive ? cfg.color : 'rgba(255,255,255,0.1)',
                      boxShadow: isActive ? `0 0 5px ${cfg.color}` : 'none',
                      transition: 'all 0.4s ease',
                    }} />
                    {cfg.icon}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
